(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./ai-chat-state.js'), null);
    return;
  }

  if (root) {
    root.MarineMaxAIChatNavigation = factory(root.MarineMaxAIChatState, root);
  }
})(typeof window !== 'undefined' ? window : null, function (state, root) {
  'use strict';

  if (!state) {
    throw new Error('MarineMax AI Chat state is required by the navigation module');
  }

  const continueIcon =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.5 15L12.5 10L7.5 5"/></svg>';
  const transientContinueLabels = [
    'sending...',
    'verifying...',
    'creating quote...',
    'sending quote...',
  ];

  function createNavigationController(options = {}) {
    const chatRoot = options.root;
    const stateManager = options.stateManager;
    const messages = options.messages;
    const conversation = options.conversation;
    const controls = options.controls;
    const captureViewData = options.captureViewData || (() => null);
    const restoreStructuredView = options.restoreStructuredView || (() => false);
    const resolveContinueDisabled =
      options.resolveContinueDisabled ||
      ((_snapshot, _restoredStructuredView, disabled) => disabled);
    const clearFormErrors = options.clearFormErrors || (() => {});
    const resizeInput = options.resizeInput || (() => {});
    const isDesktopFormOnly = options.isDesktopFormOnly || (() => false);
    const defaultInputPlaceholder = String(options.defaultInputPlaceholder || '');
    const getComputedStyle =
      options.getComputedStyle ||
      (root && root.getComputedStyle ? root.getComputedStyle.bind(root) : null);

    if (!chatRoot || !stateManager || !messages || !conversation || !controls) {
      throw new Error('AI Chat navigation dependencies are incomplete');
    }
    if (!getComputedStyle) {
      throw new Error('getComputedStyle is required by the AI Chat navigation module');
    }

    const chatBox = chatRoot.querySelector('.ai-chat__chat');
    const input = chatRoot.querySelector('.ai-chat__input textarea');
    const sendButton = chatRoot.querySelector('.ai-chat__send-btn');
    const suggestionContainer = chatRoot.querySelector('.ai-chat__suggest');
    const backButton = chatRoot.querySelector('.ai-chat__back');
    const expertContainer = chatRoot.querySelector('.ai-chat__hubspot');
    const formContainer = chatRoot.querySelector('.ai-chat__hubspot-form');
    const successSplash = chatRoot.querySelector('.ai-chat__hubspot-splash');
    const header = chatRoot.querySelector('.ai-chat__header');
    const inputWrapper = chatRoot.querySelector('.ai-chat__input-wrapper');
    const inputField = chatRoot.querySelector('.ai-chat__input');
    const viewQuoteButton = chatRoot.querySelector('.ai-chat__view-quote-btn');
    const continueButton = chatRoot.querySelector('.ai-chat__continue-btn');
    const widget = chatRoot.closest('.ai-chat-widget');

    function isVisible(element) {
      return Boolean(
        element &&
        element.style.display !== 'none' &&
        getComputedStyle(element).display !== 'none',
      );
    }

    function scrollHeaderToTop() {
      if (header) header.scrollTop = 0;
    }

    function syncHeaderMode() {
      if (!backButton) return;

      const backVisible = isVisible(backButton);
      const suggestionsVisible = isVisible(suggestionContainer);
      const expertVisible = isVisible(expertContainer);
      chatRoot.classList.toggle(
        'ai-chat--back-mode',
        backVisible && !suggestionsVisible,
      );
      chatRoot.classList.toggle('ai-chat--expert-mode', expertVisible);
      if (widget) {
        widget.classList.toggle('ai-chat-widget--expert-mode', expertVisible);
      }
    }

    function getStableContinueHtml(snapshot) {
      const html = String(snapshot.continueBtnHTML || '');
      const normalizedHtml = html.toLowerCase();
      const isTransient = transientContinueLabels.some((label) =>
        normalizedHtml.includes(label),
      );
      if (!isTransient) return html;

      const label = snapshot.continueBtnStep === 'verification-code'
        ? 'Verify Code'
        : 'Continue';
      return `${label} ${continueIcon}`;
    }

    function saveState() {
      const snapshot = {
        messages: messages.store.snapshot(),
        viewData: captureViewData(stateManager.currentView),
        suggestWrapperVisible: suggestionContainer
          ? suggestionContainer.style.display !== 'none'
          : false,
        backBtnVisible: backButton ? backButton.style.display !== 'none' : false,
        inputPlaceholder: input ? input.placeholder : '',
        inputValue: input ? input.value : '',
        sendBtnVisible: sendButton ? sendButton.style.display !== 'none' : false,
        viewQuoteBtnVisible: viewQuoteButton
          ? viewQuoteButton.style.display !== 'none'
          : false,
        continueBtnVisible: continueButton
          ? continueButton.style.display !== 'none'
          : false,
        continueBtnStep: continueButton ? continueButton.dataset.step : '',
        continueBtnHTML: continueButton ? continueButton.innerHTML : '',
        continueBtnDisabled: continueButton ? continueButton.disabled : false,
        expertWrapperVisible: expertContainer
          ? expertContainer.style.display !== 'none'
          : false,
        inputWrapperVisible: inputWrapper
          ? inputWrapper.style.display !== 'none'
          : false,
        inputFieldVisible: inputField ? inputField.style.display !== 'none' : false,
        conversationId: conversation.conversationId,
        chatBoxDataset: chatBox ? Object.assign({}, chatBox.dataset) : {},
      };
      return stateManager.push(snapshot);
    }

    function restoreState() {
      const snapshot = stateManager.pop();
      if (!snapshot) return false;

      if (chatBox && snapshot.chatBoxDataset) {
        Object.keys(snapshot.chatBoxDataset).forEach((key) => {
          chatBox.dataset[key] = snapshot.chatBoxDataset[key];
        });
      }

      messages.store.replace(snapshot.messages || []);
      const restoredStructuredView = restoreStructuredView(snapshot);
      if (!restoredStructuredView) messages.render();

      if (suggestionContainer) {
        suggestionContainer.style.display = snapshot.suggestWrapperVisible
          ? 'flex'
          : 'none';
      }
      if (backButton) {
        backButton.style.display = snapshot.backBtnVisible ? 'flex' : 'none';
      }
      if (input) {
        input.placeholder = snapshot.inputPlaceholder || defaultInputPlaceholder;
        if (snapshot.inputValue !== undefined) input.value = snapshot.inputValue;
        resizeInput();
      }
      if (sendButton) {
        sendButton.style.display = snapshot.sendBtnVisible ? '' : 'none';
      }
      if (viewQuoteButton) {
        viewQuoteButton.style.display = snapshot.viewQuoteBtnVisible ? 'flex' : 'none';
      }
      if (continueButton) {
        continueButton.style.display = snapshot.continueBtnVisible ? 'flex' : 'none';
        if (snapshot.continueBtnStep !== undefined) {
          continueButton.dataset.step = snapshot.continueBtnStep;
        }
        if (snapshot.continueBtnHTML) {
          continueButton.innerHTML = getStableContinueHtml(snapshot);
        }
        if (snapshot.continueBtnDisabled !== undefined) {
          const continueDisabled = Boolean(
            resolveContinueDisabled(
              snapshot,
              restoredStructuredView,
              snapshot.continueBtnDisabled,
            ),
          );
          continueButton.disabled = continueDisabled;
          continueButton.classList.toggle('disabled', continueDisabled);
        }
      }
      if (expertContainer) {
        expertContainer.style.display = snapshot.expertWrapperVisible
          ? 'block'
          : 'none';
      }
      if (inputWrapper) {
        inputWrapper.style.display = snapshot.inputWrapperVisible ? '' : 'none';
      }
      if (inputField) {
        inputField.style.display = snapshot.inputFieldVisible ? '' : 'none';
      }

      syncHeaderMode();
      if (snapshot.conversationId) {
        conversation.setConversationId(snapshot.conversationId);
      }
      return true;
    }

    function showChat(options = {}) {
      if (isDesktopFormOnly() && options.allowDesktopFormExit !== true) {
        showExpert({ savePreviousState: false });
        return false;
      }

      conversation.cancel();
      controls.setDisabled(false);
      stateManager.reset(state.views.CHAT_HOME);
      scrollHeaderToTop();
      chatRoot.classList.remove('is-hubspot-complete');
      if (expertContainer) expertContainer.style.display = 'none';
      if (successSplash) successSplash.style.display = 'none';
      if (formContainer) formContainer.style.display = 'block';
      if (chatBox) {
        chatBox.style.display = 'block';
        messages.render();
      }
      if (suggestionContainer) suggestionContainer.style.display = 'flex';
      if (backButton) backButton.style.display = 'none';
      if (inputWrapper) inputWrapper.style.display = '';
      if (inputField) inputField.style.display = '';
      if (input) {
        input.placeholder = defaultInputPlaceholder;
        resizeInput();
      }
      if (sendButton) sendButton.style.display = '';
      if (viewQuoteButton) viewQuoteButton.style.display = 'none';
      if (continueButton) continueButton.style.display = 'none';
      syncHeaderMode();
      return true;
    }

    function showExpert(options = {}) {
      if (options.savePreviousState !== false) saveState();

      stateManager.setView(state.views.HUBSPOT_FORM);
      scrollHeaderToTop();
      chatRoot.classList.remove('is-hubspot-complete');
      if (expertContainer) expertContainer.style.display = 'block';
      if (chatBox) chatBox.style.display = 'none';
      if (suggestionContainer) suggestionContainer.style.display = 'none';
      if (inputWrapper) inputWrapper.style.display = 'none';
      if (successSplash) successSplash.style.display = 'none';
      if (formContainer) formContainer.style.display = 'block';
      clearFormErrors();
      syncHeaderMode();
    }

    function showHubspotSuccess(options = {}) {
      const useNativeMessage = options.native === true;
      stateManager.setView(state.views.HUBSPOT_SUCCESS);
      chatRoot.classList.add('is-hubspot-complete');
      if (formContainer) {
        formContainer.style.display = useNativeMessage ? 'block' : 'none';
        if (useNativeMessage) formContainer.scrollTop = 0;
      }
      if (successSplash) successSplash.style.display = useNativeMessage ? 'none' : 'flex';
      if (useNativeMessage && expertContainer) expertContainer.scrollTop = 0;
    }

    function prepareConversationView() {
      if (suggestionContainer && suggestionContainer.style.display !== 'none') {
        saveState();
        suggestionContainer.style.display = 'none';
        scrollHeaderToTop();
        if (backButton) backButton.style.display = 'flex';
      }
      stateManager.setView(state.views.CHAT_CONVERSATION);
    }

    function resetToHome() {
      showChat();
      messages.clear();
      conversation.setConversationId(null);
    }

    function handleBack() {
      if (isDesktopFormOnly()) return false;

      const leavingConversation =
        stateManager.currentView === state.views.CHAT_CONVERSATION ||
        stateManager.currentView === state.views.FREEFORM_PROMPT;
      if (
        leavingConversation ||
        !restoreState() ||
        stateManager.currentView === state.views.CHAT_HOME
      ) {
        resetToHome();
      } else {
        conversation.cancel();
        controls.setDisabled(false);
      }
      if (input) {
        resizeInput({ reset: true });
        input.focus();
      }
      return true;
    }

    return Object.freeze({
      handleBack,
      prepareConversationView,
      restoreState,
      saveState,
      scrollHeaderToTop,
      showChat,
      showExpert,
      showHubspotSuccess,
      syncHeaderMode,
    });
  }

  return Object.freeze({
    createNavigationController,
  });
});
